import { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    Typography,
    Paper,
    Grid,
    Box,
    Avatar,
    CssBaseline
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DashboardAuth from '../DashboardAuth';

const DashboardLockScreen = () => {
    const [pin, setPin] = useState('');
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        const unlocked = sessionStorage.getItem('dashboard_unlocked');
        if (unlocked === 'true') {
            setIsUnlocked(true);
        }
    }, []);

    const handleUnlock = () => {
        const correctPin = process.env.REACT_APP_DASHBOARD_PIN;

        if (pin === correctPin) {
            sessionStorage.setItem('dashboard_unlocked', 'true');
            setIsUnlocked(true);
            window.location.reload();
        } else {
            setError(true);
            setPin('');
        }
    };

    if (isUnlocked) {
        return <DashboardAuth />;
    }

    return (
        <Grid container component="main" sx={{ height: '100vh', bgcolor: 'background.default' }}>
            <CssBaseline />
            <Grid
                item
                xs={12}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Paper
                    elevation={24}
                    sx={{
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        maxWidth: 400,
                        width: '100%',
                        mx: 2
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'secondary.main', color: 'secondary.contrastText', width: 56, height: 56 }}>
                        <LockOutlinedIcon fontSize="large" />
                    </Avatar>

                    <Typography component="h1" variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mt: 1 }}>
                        Restricted Access
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, mt: 1 }}>
                        This dashboard is protected. Enter PIN.
                    </Typography>

                    <Box component="div" sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="pin"
                            label="Security PIN"
                            type="password"
                            id="pin"
                            autoComplete="current-password"
                            value={pin}
                            onChange={(e) => {
                                setPin(e.target.value);
                                setError(false);
                            }}
                            error={error}
                            helperText={error ? "Incorrect PIN provided" : ""}
                            autoFocus
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: 'text.primary',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.8rem',
                                    height: '60px',
                                    textAlign: 'center',
                                    '& fieldset': {
                                        borderColor: error ? 'error.main' : 'divider',
                                        borderWidth: error ? '2px' : '1px',
                                        transition: 'all 0.2s ease-in-out'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: error ? 'error.main' : 'text.primary'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: error ? 'error.main' : 'secondary.main',
                                        borderWidth: '2px'
                                    },
                                },
                                '& .MuiInputLabel-root': { color: error ? 'error.main' : 'text.secondary' },
                                '& .MuiInputLabel-root.Mui-focused': { color: error ? 'error.main' : 'secondary.main' },
                                '& input': { textAlign: 'center' }
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleUnlock();
                                }
                            }}
                        />

                        <Button
                            type="button"
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleUnlock}
                            sx={{
                                mt: 3,
                                mb: 2,
                                py: 1.5,
                                bgcolor: 'secondary.main',
                                color: 'secondary.contrastText',
                                fontWeight: 'bold',
                                letterSpacing: '1px',
                                textTransform: 'none',
                                fontSize: '1rem',
                                '&:hover': {
                                    bgcolor: 'secondary.dark'
                                }
                            }}
                        >
                            Unlock Dashboard
                        </Button>
                    </Box>
                </Paper>

                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 4 }}>
                    Authorized Personnel Only
                </Typography>
            </Grid>
        </Grid>
    );
};

export default DashboardLockScreen;